import { useEffect, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import GradientBackground from '../components/GradientBackground';
import colors from '../theme';
import { RootStackParamList } from '../navigation/RootNavigator';
import { AudiobookTopic, getTopics } from '../api/audiobooks';

type Props = NativeStackScreenProps<RootStackParamList, 'PreferencesGenre'>;

export default function PreferencesGenreScreen({ navigation }: Props) {
  const [topics, setTopics] = useState<AudiobookTopic[]>([]);
  const [selected, setSelected] = useState<number[]>([]);

  useEffect(() => {
    getTopics().then(setTopics).catch(() => setTopics([]));
  }, []);

  const toggleTopic = (id: number) => {
    setSelected((current) => (
      current.includes(id) ? current.filter((item) => item !== id) : [...current, id]
    ));
  };

  return (
    <View style={styles.screen}>
      <GradientBackground fullHeight />
      <ScrollView contentContainerStyle={styles.container} showsVerticalScrollIndicator={false}>
        <View style={styles.topRow}>
          <Pressable onPress={() => navigation.navigate('Home')} hitSlop={10}>
            <Text style={styles.skip}>Bỏ qua</Text>
          </Pressable>
        </View>
        <Text style={styles.title}>Bạn thích nghe chủ đề nào?</Text>
        <Text style={styles.subtitle}>Chọn vài chủ đề để ứng dụng gợi ý sách nói phù hợp hơn.</Text>
        <View style={styles.progressBarOuter}>
          <View style={styles.progressBarInner} />
        </View>
        <View style={styles.grid}>
          {topics.map((topic) => {
            const active = selected.includes(topic.id);
            return (
              <Pressable key={topic.id} style={[styles.topicCard, active && styles.topicCardActive]} onPress={() => toggleTopic(topic.id)}>
                <Text style={[styles.topicName, active && styles.topicNameActive]} numberOfLines={2}>{topic.name}</Text>
              </Pressable>
            );
          })}
        </View>
        <View style={styles.actions}>
          <Pressable style={styles.altButton} onPress={() => navigation.navigate('Home')}>
            <Text style={styles.altButtonText}>Bỏ qua</Text>
          </Pressable>
          <Pressable style={styles.mainButton} onPress={() => navigation.navigate('PreferencesAuthors', { topicIds: selected })}>
            <Text style={styles.mainButtonText}>Tiếp tục</Text>
          </Pressable>
        </View>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: 'rgb(242, 249, 253)',
  },
  container: {
    flexGrow: 1,
    paddingHorizontal: 24,
    paddingTop: 44,
    paddingBottom: 34,
  },
  topRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginBottom: 20,
  },
  skip: {
    color: colors.buttonText,
    fontSize: 14,
    fontWeight: '900',
  },
  title: {
    color: colors.buttonText,
    fontSize: 22,
    fontWeight: '900',
    marginBottom: 10,
  },
  subtitle: {
    color: colors.button,
    fontSize: 14,
    lineHeight: 20,
    marginBottom: 20,
    fontWeight: '800',
  },
  progressBarOuter: {
    height: 7,
    borderRadius: 999,
    backgroundColor: 'rgba(255,255,255,0.6)',
    overflow: 'hidden',
    marginBottom: 22,
  },
  progressBarInner: {
    width: '50%',
    height: '100%',
    backgroundColor: colors.button,
  },
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    marginBottom: 32,
  },
  topicCard: {
    width: '48%',
    minHeight: 76,
    borderRadius: 22,
    backgroundColor: 'rgba(255,255,255,0.92)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 12,
    marginBottom: 12,
  },
  topicCardActive: {
    backgroundColor: colors.button,
  },
  topicName: {
    color: colors.textPrimary,
    fontSize: 14,
    fontWeight: '900',
    textAlign: 'center',
  },
  topicNameActive: {
    color: colors.buttonText,
  },
  actions: {
    flexDirection: 'row',
    gap: 12,
  },
  altButton: {
    flex: 1,
    backgroundColor: 'rgba(255,255,255,0.9)',
    borderRadius: 18,
    height: 48,
    alignItems: 'center',
    justifyContent: 'center',
  },
  altButtonText: {
    color: colors.textSecondary,
    fontWeight: '900',
    fontSize: 14,
  },
  mainButton: {
    flex: 1,
    backgroundColor: colors.button,
    borderRadius: 18,
    height: 48,
    alignItems: 'center',
    justifyContent: 'center',
  },
  mainButtonText: {
    color: colors.buttonText,
    fontWeight: '900',
    fontSize: 14,
  },
});
